import React from 'react';

import DataTable from '../shared/data_table';
import StatusPill from '../shared/status_pill';
import { Ledger, Note, StatusCell, What, Who } from '../shared/cells';
import WhyPopover from '../overview/why_popover';
import { ENTRY_TYPE } from './params';

/* saved entries. */
// highlight_uuid: the entry an IDEMPOTENCY_MISMATCH says was already saved — the operator lands on it.
// sort/on_sort: server-side (additional/types.go ListSortKeys) — a header click re-fetches the page.
const EntriesTable = ({ rows, on_edit, on_remove, highlight_uuid, sort, on_sort, on_sort_set }) => {
  // Table: compact links; cards (touch): full-size buttons in the footer.
  const action_buttons = (r, className) => (
    <>
      {r.can_edit ? (
        <button type="button" className={className} onClick={() => on_edit(r)}>
          Edit
        </button>
      ) : null}
      {r.can_remove ? (
        <button type="button" className={className} onClick={() => on_remove(r)}>
          Remove
        </button>
      ) : null}
      {r.can_waive ? (
        <button type="button" className={className} onClick={() => on_remove(r)}>
          Waive
        </button>
      ) : null}
    </>
  );
  const can_act = (r) => r.can_edit || r.can_remove || r.can_waive;
  const why = (r) => <div className="tp-sub tp-ap-why">{r.why_not || 'Not editable'}</div>;
  const actions_cell = (r) => (
    <div className="tp-ap-act">
      {can_act(r) ? (
        <span className="tp-c-acts tp-actions">{action_buttons(r, 'tp-link')}</span>
      ) : (
        why(r)
      )}
    </div>
  );
  const actions_card = (r) => (
    <div className="tp-ap-act">
      {can_act(r) ? (
        <div className="tp-row-actions tp-actions">{action_buttons(r, 'tp-btn-out')}</div>
      ) : (
        why(r)
      )}
    </div>
  );

  const status_chip = (r) => (
    <StatusPill tone={r.is_removed ? 'red' : 'green'}>{r.status_label}</StatusPill>
  );
  const status_meta = (r) => [
    r.reversed_text || null,
    r.is_removed && !r.reversed_text && r.removed_at_text ? (
      <span title={r.remove_reason}>
        Removed by <span title={r.removed_by_title}>{r.removed_by_text}</span> · {r.removed_at_text}
      </span>
    ) : null
  ];
  const amount = (r) => (
    <WhyPopover
      detail={r.explain}
      amount_node={<span className={r.amount_tone}>{r.amount_text}</span>}
    />
  );
  const has_date = (r) => r.entry_at_text && r.entry_at_text !== '—';

  // Four tracks (list-row pattern): who · entry · amount · status and actions; the note, reference,
  // source and who added it run under the row from the entry track. Nothing folds; cards below 660px.
  const columns = [
    {
      key: 'teacher_name',
      role: 'identifier',
      label: 'Teacher',
      track: { compact: '28%', full: '26%' },
      sort_key: 'teacher_name',
      // Source and Added by live in the note line, which has no header: they sort from here.
      sorts: [
        { sort_key: 'date', label: 'Date / time (IST)' },
        { sort_key: 'source', label: 'Source' },
        { sort_key: 'added_by', label: 'Added by' }
      ],
      render: (r) => (
        <Who name={r.teacher_name} meta={[has_date(r) ? r.entry_at_text : null, r.teacher_slug]} />
      )
    },
    {
      key: 'category_label',
      label: 'Entry',
      sort_key: 'category',
      sorts: [{ sort_key: 'type', label: 'Type' }],
      render: (r) => (
        <What
          title={r.category_label}
          lines={[
            <span className="tp-c-inline">
              <StatusPill tone={r.entry_type === ENTRY_TYPE.DEDUCTION ? 'orange' : 'green'}>
                {r.entry_type_label}
              </StatusPill>
              <span>for {r.month_text}</span>
            </span>,
            r.is_system && r.source_month_text ? `From ${r.source_month_text}` : null
          ]}
        />
      )
    },
    {
      key: 'amount_text',
      role: 'amount',
      label: 'Amount',
      align: 'right',
      // Wide enough for a signed crore amount to the paisa.
      track: { compact: '156px', full: '160px' },
      sort_key: 'amount',
      render: (r) => <span className="tp-c-amt">{amount(r)}</span>,
      // In a card the amount reads as a labelled ledger line, value on the right.
      card_render: (r) => <Ledger items={[{ label: 'Amount', value: amount(r) }]} />
    },
    {
      key: 'status',
      role: 'status',
      label: 'Status',
      track: { compact: '140px', full: '168px' },
      sort_key: 'status',
      render: (r) => (
        <div className="tp-ap-status">
          <StatusCell chip={status_chip(r)} meta={status_meta(r)} actions={actions_cell(r)} />
        </div>
      ),
      card_render: (r) => <StatusCell chip={status_chip(r)} meta={status_meta(r)} />
    },
    {
      key: 'note',
      label: 'Note',
      line: { from: 'category_label', bare: true },
      render: (r) => (
        <Note
          text={r.note || null}
          by={
            <span className="tp-c-meta">
              {r.reference_id ? (
                <span>
                  Ref <span className="tp-mono">{r.reference_id}</span>
                </span>
              ) : null}
              <span>
                Source{' '}
                <StatusPill tone={r.is_system_posted ? 'blue' : 'gray'}>
                  {r.source_label}
                </StatusPill>
              </span>
              {r.added_text ? <span title={r.added_by_title}>Added by {r.added_text}</span> : null}
            </span>
          }
        />
      )
    },
    {
      key: 'actions',
      role: 'actions',
      label: 'Actions',
      in_table: false,
      card_render: actions_card
    }
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.key}
      rowClassName={(r) => (highlight_uuid && r.uuid === highlight_uuid ? 'tp-row-hl' : '')}
      emptyText="No additional payments for this month and filter."
      sort={sort}
      on_sort={on_sort}
      on_sort_set={on_sort_set}
      card_below={660}
      responsive
    />
  );
};

export default EntriesTable;
